import { Component, computed, effect, input, InputSignal, Signal, ChangeDetectionStrategy } from '@angular/core';
import { ICON_NAMES, ICON_SPRITE_VERSION, IconAnimation, IconName, IconWeight } from '../../models';

type IconAnimationSpeed = 'slow' | 'normal' | 'fast';

/** Icon names of the SVG sprites */
const ICON_NAME_SET = new Set<string>(ICON_NAMES);
/** Unknown icon names that were already reported, to warn once per name */
const reportedUnknownIcons = new Set<string>();

/**
 * A component to display the Phosphor icons (https://phosphoricons.com) of the SVG sprites with animations
 * - Every weight is a sprite in `libs/shared/src/assets/icons`, see ICON_NAMES
 * - FontAwesome glyphs of CS3 data are translated with iconNameFromFontAwesome before
 *
 * Usage example:
 * ```html
 * <!-- Basic usage (light by default) -->
 * <gs-icon iconName="house"></gs-icon>
 *
 * <!-- With specific weight -->
 * <gs-icon iconName="user" iconWeight="fill"></gs-icon>
 *
 * <!-- With animation -->
 * <gs-icon iconName="circle-notch" animation="spin"></gs-icon>
 *
 * <!-- With animation speed -->
 * <gs-icon iconName="bell" iconWeight="regular" animation="shake" animationSpeed="slow"></gs-icon>
 * ```
 */
@Component({
  selector: 'gs-icon',
  imports: [],
  styles: [
    `
      :host {
        display: inline-flex;
        justify-content: center;
        align-items: center;
      }

      svg {
        width: 1em;
        height: 1em;
        fill: currentColor;
        --gs-icon-speed: 1;
        animation-duration: calc(var(--gs-icon-duration) * var(--gs-icon-speed));
        animation-iteration-count: infinite;
      }

      /* Animations like the FontAwesome ones (https://fontawesome.com/docs/web/style/animate) */
      svg[data-speed='slow'] {
        --gs-icon-speed: 2;
      }
      svg[data-speed='fast'] {
        --gs-icon-speed: 0.5;
      }
      svg[data-animation='spin'] {
        --gs-icon-duration: 2s;
        animation-name: gs-icon-spin;
        animation-timing-function: linear;
      }
      svg[data-animation='pulse'] {
        --gs-icon-duration: 1s;
        animation-name: gs-icon-spin;
        animation-timing-function: steps(8);
      }
      svg[data-animation='beat'] {
        --gs-icon-duration: 1s;
        animation-name: gs-icon-beat;
        animation-timing-function: ease-in-out;
      }
      svg[data-animation='bounce'] {
        --gs-icon-duration: 1s;
        animation-name: gs-icon-bounce;
        animation-timing-function: cubic-bezier(0.28, 0.84, 0.42, 1);
      }
      svg[data-animation='fade'] {
        --gs-icon-duration: 1s;
        animation-name: gs-icon-fade;
        animation-timing-function: cubic-bezier(0.4, 0, 0.6, 1);
      }
      svg[data-animation='beat-fade'] {
        --gs-icon-duration: 1s;
        animation-name: gs-icon-beat-fade;
        animation-timing-function: cubic-bezier(0.4, 0, 0.6, 1);
      }
      svg[data-animation='flip'] {
        --gs-icon-duration: 1s;
        animation-name: gs-icon-flip;
        animation-timing-function: ease-in-out;
      }
      svg[data-animation='shake'] {
        --gs-icon-duration: 1s;
        animation-name: gs-icon-shake;
        animation-timing-function: linear;
      }

      @media (prefers-reduced-motion: reduce) {
        svg[data-animation] {
          animation: none;
        }
      }

      @keyframes gs-icon-spin {
        to {
          transform: rotate(1turn);
        }
      }
      @keyframes gs-icon-beat {
        0%,
        90% {
          transform: scale(1);
        }
        45% {
          transform: scale(1.25);
        }
      }
      @keyframes gs-icon-bounce {
        0%,
        64%,
        to {
          transform: scale(1) translateY(0);
        }
        10% {
          transform: scale(1.1, 0.9) translateY(0);
        }
        30% {
          transform: scale(0.9, 1.1) translateY(-0.5em);
        }
        50% {
          transform: scale(1.05, 0.95) translateY(0);
        }
        57% {
          transform: scale(1) translateY(-0.125em);
        }
      }
      @keyframes gs-icon-fade {
        50% {
          opacity: 0.4;
        }
      }
      @keyframes gs-icon-beat-fade {
        0%,
        to {
          opacity: 0.4;
          transform: scale(1);
        }
        50% {
          opacity: 1;
          transform: scale(1.125);
        }
      }
      @keyframes gs-icon-flip {
        50% {
          transform: rotateY(-180deg);
        }
      }
      @keyframes gs-icon-shake {
        0% {
          transform: rotate(-15deg);
        }
        4% {
          transform: rotate(15deg);
        }
        8%,
        24% {
          transform: rotate(-18deg);
        }
        12%,
        28% {
          transform: rotate(18deg);
        }
        16% {
          transform: rotate(-22deg);
        }
        20% {
          transform: rotate(22deg);
        }
        32% {
          transform: rotate(-12deg);
        }
        36% {
          transform: rotate(12deg);
        }
        40%,
        to {
          transform: rotate(0deg);
        }
      }
    `
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <svg
      [attr.data-animation]="activeAnimation()"
      [attr.data-speed]="activeAnimation() && animationSpeed()"
      [attr.aria-hidden]="ariaHidden()"
      focusable="false"
    >
      <use [attr.href]="spriteHref()"></use>
    </svg>
  `
})
export class GsIconComponent {
  // Inputs & Outputs
  /** The name of the icon, one of ICON_NAMES (e.g. "magnifying-glass") */
  iconName: InputSignal<IconName> = input.required<IconName>();
  /** The weight of the icon - defaults to light, also when bound to an icon without weight */
  iconWeight: InputSignal<IconWeight> = input<IconWeight>('light');
  /** Optional animation to apply to the icon - no animation by default */
  animation: InputSignal<IconAnimation | undefined> = input<IconAnimation | undefined>(undefined);
  /** Speed of the animation (slow, normal, fast) */
  animationSpeed: InputSignal<IconAnimationSpeed> = input<IconAnimationSpeed>('normal');
  /** Whether the icon should be hidden from screen readers */
  ariaHidden: InputSignal<boolean> = input<boolean>(true);

  // Properties & Signals
  /** Sprite reference of the icon, versioned by the sprite content because the servers cache assets for a year */
  spriteHref: Signal<string> = computed(
    () => `shared/assets/icons/${this.iconWeight() ?? 'light'}.svg?v=${ICON_SPRITE_VERSION}#${this.iconName()}`
  );
  /** Animation of the icon, null without animation so the attribute is left out */
  activeAnimation: Signal<IconAnimation | null> = computed(() => {
    const animation = this.animation();
    return animation && animation !== 'none' ? animation : null;
  });

  constructor() {
    // Reports names that are not in the sprites, they render nothing
    effect(() => {
      const iconName = this.iconName();
      if (iconName && !ICON_NAME_SET.has(iconName) && !reportedUnknownIcons.has(iconName)) {
        reportedUnknownIcons.add(iconName);
        console.warn(`[gs-icon] Unknown icon "${iconName}", add it to ICON_NAMES`);
      }
    });
  }
}
